/**
 * „Heute" — der erste Bereich der Monteur-App, `07` §7.7.
 *
 * Das Dokument nennt fünf Dinge: Begrüßung und Synchronisationsstatus, der
 * aktuelle und die folgenden Einsätze, Zeitfenster mit Kunde, Adresse und
 * Priorität, Navigation und Anruf, und die Warnung bei offline nicht
 * verfügbaren Pflichtunterlagen.
 *
 * **Der laufende Einsatz steht oben und groß.** Ein Monteur öffnet die App
 * nicht, um eine Liste zu lesen — er öffnet sie, weil er etwas tun oder
 * nachsehen will, und in neun von zehn Fällen betrifft das den Einsatz, in
 * dem er gerade steckt. Eine gleichförmige Liste zwingt ihn jedes Mal, ihn
 * darin zu suchen.
 *
 * **Navigation und Anruf sind echte Links** — `geo:` und `tel:`. Nach ADR-018
 * gibt es keine Anrufe in der Anwendung; für Sprache bleibt das Telefon, und
 * `07` §7.7 nennt den `tel:`-Link ausdrücklich. Ein Knopf, der die Nummer nur
 * anzeigt, wäre auf einer Leiter der falsche Weg.
 */

import { Link } from "react-router";

import { findeAuftrag, findeKunde } from "@hwc/demo-data";
import type { Einsatz } from "@hwc/demo-data";

import { useHeute, useKommend, useMonteur } from "../../src/Sitzung.js";
import {
  Einsatzabzeichen,
  Leer,
  Ortszeile,
  Wartemarke,
  datum,
  uhrzeit,
  zeitfenster,
} from "../../src/bausteine.js";

/** Die Zustände, in denen ein Einsatz gerade bearbeitet wird. */
const LAEUFT = ["anfahrt", "vor-ort", "wartet"] as const;

/**
 * Die Zustände, in denen der Monteur nichts mehr zu tun hat.
 *
 * `uebermittelt` gehört dazu, obwohl der Einsatz noch nicht endgültig ist:
 * Nach ADR-045 liegt der nächste Schritt beim Büro — `uebermittelt →
 * geprueft` und die Rückgabe kann der Monteur nicht auslösen. Für ihn ist der
 * Einsatz damit durch, und die Tagesansicht sagt das.
 */
const FERTIG = ["uebermittelt", "geprueft", "storniert"] as const;

export function Heute() {
  const { monteur, heute, zustandVon, online, lokal } = useMonteur();
  const einsaetze = useHeute();
  const kommend = useKommend();

  const laufend = einsaetze.find((e) =>
    (LAEUFT as readonly string[]).includes(zustandVon(e)),
  );

  // **Erledigtes gehört nicht unter „Danach".** Der erste Entwurf teilte den
  // Tag nur in „läuft gerade" und „der Rest" — und stellte damit den
  // Notdienst von 06:45, längst übermittelt, unter die Überschrift „Danach
  // heute". Wer die Liste überfliegt, liest dort, was noch kommt; ein
  // abgeschlossener Einsatz an dieser Stelle kostet jedes Mal einen zweiten
  // Blick, um ihn wieder auszusortieren.
  const durch = einsaetze.filter(
    (e) => e !== laufend && (FERTIG as readonly string[]).includes(zustandVon(e)),
  );
  const rest = einsaetze.filter(
    (e) => e !== laufend && !(FERTIG as readonly string[]).includes(zustandVon(e)),
  );

  const stunde = heute.getHours();
  const gruss = stunde < 11 ? "Guten Morgen" : stunde < 18 ? "Hallo" : "Guten Abend";

  return (
    <>
      <header className="tageskopf">
        <h1>
          {gruss}, {monteur.name.split(" ")[0]}
        </h1>
        <p className="leise">
          {datum(heute.toISOString())} ·{" "}
          {einsaetze.length === 0
            ? "keine Einsätze"
            : `${einsaetze.length} ${einsaetze.length === 1 ? "Einsatz" : "Einsätze"}`}
        </p>
      </header>

      {/*
        Der Synchronisationsstatus steht oben und nicht in den Einstellungen.
        `07` §7.9 verlangt, dass die App klar unterscheidet — und die
        Unterscheidung nützt nichts an einer Stelle, die man aufsuchen muss.
      */}
      <div className={`syncleiste syncleiste--${online ? "verbunden" : "offline"}`}>
        {online ? (
          lokal.warteschlange.length > 0 ? (
            <>
              <strong>{lokal.warteschlange.length} wartet auf Upload.</strong>{" "}
              <Link to="/mehr">Jetzt senden</Link>
            </>
          ) : (
            <>Alles synchronisiert.</>
          )
        ) : (
          <>
            <strong>Offline.</strong> Weiterarbeiten geht — was erfasst wird, geht raus,
            sobald wieder Netz da ist
            {lokal.warteschlange.length > 0 && ` (${lokal.warteschlange.length} wartet)`}.
          </>
        )}
      </div>

      {laufend && (
        <>
          <h2>Läuft gerade</h2>
          <Einsatzkarte einsatz={laufend} gross />
        </>
      )}

      <h2>{laufend ? "Danach heute" : "Heute"}</h2>
      {rest.length === 0 ? (
        <Leer
          text={
            laufend
              ? "Danach steht heute nichts mehr an."
              : "Für heute ist nichts eingeplant."
          }
        />
      ) : (
        rest.map((e) => <Einsatzkarte key={e.ref.opaqueId} einsatz={e} />)
      )}

      {durch.length > 0 && (
        <>
          <h2>Heute schon erledigt</h2>
          {durch.map((e) => (
            <Einsatzkarte key={e.ref.opaqueId} einsatz={e} />
          ))}
        </>
      )}

      {kommend.length > 0 && (
        <>
          <h2>Als Nächstes</h2>
          {kommend.slice(0, 3).map((e) => (
            <Einsatzkarte key={e.ref.opaqueId} einsatz={e} mitDatum />
          ))}
          {kommend.length > 3 && (
            <p>
              <Link to="/einsaetze">Alle {kommend.length} kommenden Einsätze</Link>
            </p>
          )}
        </>
      )}
    </>
  );
}

export function Einsatzkarte({
  einsatz,
  gross = false,
  mitDatum = false,
}: {
  einsatz: Einsatz;
  gross?: boolean;
  mitDatum?: boolean;
}) {
  const { bestand, zustandVon, wartet, auflage } = useMonteur();

  const auftrag = findeAuftrag(bestand, einsatz.auftrag);
  const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;
  const ort = auftrag?.einsatzort ?? kunde?.anschrift;
  const zustand = zustandVon(einsatz);
  const offen = wartet(einsatz);

  // `07` §7.9: Warnung bei offline nicht verfügbaren Pflichtunterlagen. Was
  // „Pflicht" ist, entscheidet hier die Kennzeichnung am Objekt — im Zielbild
  // die Vorladeregel des Adapters.
  const unterlagen = bestand.ablage.filter(
    (o) => o.offline === "nur-online" && o.pfad?.includes(auftrag?.nummer ?? " "),
  );

  const eigeneSchritte = auflage(einsatz).erledigteSchritte;
  const erledigt = einsatz.schritte.filter(
    (s) => s.erledigt || eigeneSchritte.includes(s.ref.opaqueId),
  ).length;

  // Die Telefonnummer nur, wenn sie für Monteure freigegeben ist — `07` §7.9
  // lädt ausdrücklich nur die freigegebene vor.
  const kontakt = kunde?.kontakte.find((k) => k.fuerMonteur && k.telefon);

  return (
    <article className={`einsatzkarte${gross ? " einsatzkarte--gross" : ""}`}>
      <div className="einsatzkarte__kopf">
        <span className="einsatzkarte__zeit">
          {mitDatum && <>{datum(einsatz.von)} · </>}
          {zeitfenster(einsatz.von, einsatz.bis)}
        </span>
        <Einsatzabzeichen zustand={zustand} />
      </div>

      <Link className="einsatzkarte__titel" to={`/einsatz/${einsatz.ref.opaqueId}`}>
        {einsatz.titel}
      </Link>

      <p className="einsatzkarte__kunde">
        {kunde?.name ?? "unbekannter Kunde"}
        {auftrag && <> · Auftrag {auftrag.nummer}</>}
      </p>

      {ort && <Ortszeile anschrift={ort} />}

      {einsatz.prioritaet === "notfall" && (
        <p className="marke marke--notfall">Notfall</p>
      )}
      {einsatz.wartetAuf && (
        <p className="marke marke--warten">Wartet: {einsatz.wartetAuf}</p>
      )}
      {einsatz.hinweis && <p className="einsatzkarte__hinweis">{einsatz.hinweis}</p>}

      {unterlagen.length > 0 && (
        <p className="marke marke--warten">
          {unterlagen.length} Unterlage{unterlagen.length === 1 ? "" : "n"} ist nur online
          verfügbar. Vor der Abfahrt öffnen, wenn sie gebraucht wird.
        </p>
      )}

      <p className="einsatzkarte__fortschritt leise">
        {erledigt} von {einsatz.schritte.length} Schritten
        {offen > 0 && (
          <>
            {" · "}
            <Wartemarke anzahl={offen} />
          </>
        )}
      </p>

      {/*
        Zwei Aktionen, die ohne Umweg funktionieren müssen. Auf einer Leiter
        oder mit Handschuhen ist der Weg über „Kunde öffnen, Nummer suchen,
        markieren, wählen" keiner.
      */}
      <div className="einsatzkarte__aktionen">
        {ort && (
          <a
            className="knopfleiste__knopf"
            href={`geo:0,0?q=${encodeURIComponent(
              `${ort.strasse}, ${ort.plz} ${ort.ort}`,
            )}`}
          >
            Navigation
          </a>
        )}
        {kontakt?.telefon && (
          <a
            className="knopfleiste__knopf"
            href={`tel:${kontakt.telefon.replace(/\s/g, "")}`}
          >
            {kontakt.name} anrufen
          </a>
        )}
        <Link className="knopfleiste__knopf knopfleiste__knopf--haupt"
          to={`/einsatz/${einsatz.ref.opaqueId}`}>
          Öffnen
        </Link>
      </div>

      {einsatz.tatsaechlichAngekommen && (
        <p className="leise einsatzkarte__fortschritt">
          Angekommen {uhrzeit(einsatz.tatsaechlichAngekommen)}
        </p>
      )}
    </article>
  );
}
